import React, { useEffect, useMemo, useRef, useState } from "react";
import { toLocalYmd } from "../utils/localDate";
import { formatStoredMoney } from "../utils/currency";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import { FaDownload, FaFileExcel, FaPrint, FaTimes } from "react-icons/fa";
import { downloadExcel, downloadPDF, printThermal } from "../utils/exportHelpers";
import { buildSplitReportFromOrders } from "../utils/splitPaymentReport";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";
import { API_ENDPOINTS } from "../config/constants";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const DEFAULT_REPORT = (date) => ({
  date,
  restaurant: "",
  total_split_orders: 0,
  total_split_amount: 0,
  totals_by_mode: { cash: 0, upi: 0, card: 0, credit: 0 },
  mode_order_counts: { cash: 0, upi: 0, card: 0, credit: 0 },
  orders: [],
});

const SplitPaymentReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [report, setReport] = useState(DEFAULT_REPORT(today()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}").data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fromDate, toDate]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.GET_ORDER_LIST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: userId, fromdate: fromDate, todate: toDate }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        throw new Error(json?.message || "Failed to load report");
      }
      const list = Array.isArray(json?.list) ? json.list : [];
      const restaurantName =
        json?.restaurant ||
        JSON.parse(localStorage.getItem("userData") || "{}").data?.restaurant_name ||
        "";
      const period = `${fromDate} to ${toDate}`;
      const built = buildSplitReportFromOrders(list, period, restaurantName);
      setReport({ ...DEFAULT_REPORT(period), ...built });
    } catch (e) {
      toast.error(e.message || "Unable to load report");
      setReport(DEFAULT_REPORT(`${fromDate} to ${toDate}`));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtCurrency = formatStoredMoney;

  const formatSplitLines = (split) => {
    const lines = [];
    if (!split || typeof split !== "object") return lines;
    if (Number(split.cash) > 0) lines.push(`Cash: ${fmtCurrency(split.cash)}`);
    if (Number(split.upi) > 0) lines.push(`UPI: ${fmtCurrency(split.upi)}`);
    if (Number(split.card) > 0) lines.push(`Card: ${fmtCurrency(split.card)}`);
    if (Number(split.credit) > 0) lines.push(`Credit: ${fmtCurrency(split.credit)}`);
    return lines;
  };

  const exportExcel = () => {
    if (!hasData) {
      toast.info("No data to export.");
      return;
    }
    const headers = [
      "#",
      "Bill No",
      "Cash",
      "UPI",
      "Card",
      "Credit",
      "Total Amount",
      "Date",
    ];
    const rows = (report.orders || []).map((order, idx) => [
      idx + 1,
      order.ordercode || "-",
      order.split?.cash || 0,
      order.split?.upi || 0,
      order.split?.card || 0,
      order.split?.credit || 0,
      order.totalamount || 0,
      order.created_at ? new Date(order.created_at).toLocaleString("en-IN") : "-",
    ]);
    downloadExcel(`split_payment_report_${fromDate}_to_${toDate}.xlsx`, headers, rows);
  };

  const exportPdf = async () => downloadPDF(reportRef.current, `split_payment_report_${fromDate}_to_${toDate}.pdf`);

  const handlePrint = async () => {
    if (!hasData) {
      toast.info("No data to print.");
      return;
    }
    await printThermal(reportRef.current, `split_payment_report_${fromDate}_to_${toDate}`);
  };

  const hasData = Array.isArray(report?.orders) && report.orders.length > 0;

  const modeRows = useMemo(
    () => [
      { key: "cash", label: "Cash" },
      { key: "upi", label: "UPI" },
      { key: "card", label: "Card" },
      { key: "credit", label: "Credit" },
    ],
    []
  );

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <ReportPageHeader title="Split Payment Report" subtitle="Split orders by payment mode for the selected period." stats={[{ label: "Split Orders", value: report.total_split_orders || 0 }, { label: "Split Amount", value: fmtCurrency(report.total_split_amount) }]} />

      <ReportControlBar>
        <ReportPeriodPicker fromDate={fromDate} toDate={toDate} onChange={({ from, to }) => { setFromDate(from); setToDate(to); }} />
        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>

      <Card ref={reportRef}>
        <KpiRow>
          <KpiCard>
            <KpiLabel>Split Orders</KpiLabel>
            <KpiValue>{report.total_split_orders || 0}</KpiValue>
          </KpiCard>
          <KpiCard>
            <KpiLabel>Split Amount</KpiLabel>
            <KpiValue>{fmtCurrency(report.total_split_amount)}</KpiValue>
          </KpiCard>
        </KpiRow>

        <Section>
          <SectionTitle>Split by Mode</SectionTitle>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Mode</th>
                  <th>Orders</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {modeRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td>{report.mode_order_counts?.[row.key] || 0}</td>
                    <td>{fmtCurrency(report.totals_by_mode?.[row.key] || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Section>

        {loading ? (
          <Center>Loading…</Center>
        ) : !hasData ? (
          <Center>No split payments for this period.</Center>
        ) : (
          <Section>
            <SectionTitle>Split Payment Details</SectionTitle>
            <OrderGrid>
              {report.orders.map((order, idx) => (
                <OrderCard key={`${order?.id}-${idx}`} onClick={() => setDetailOrder(order)}>
                  <OrderTop>
                    <OrderId>#{order?.ordercode}</OrderId>
                    <OrderAmount>{fmtCurrency(order?.totalamount)}</OrderAmount>
                  </OrderTop>
                  {order?.created_at && (
                    <OrderDate>{new Date(order.created_at).toLocaleString("en-IN")}</OrderDate>
                  )}
                  {formatSplitLines(order?.split).map((line) => (
                    <OrderLine key={`${order?.id}-${line}`}>{line}</OrderLine>
                  ))}
                </OrderCard>
              ))}
            </OrderGrid>
          </Section>
        )}

        <Actions style={{ marginTop: 12 }}>
          <PrimaryBtn onClick={exportExcel}>
            <FaFileExcel /> Excel
          </PrimaryBtn>
          <PrimaryBtn onClick={exportPdf}>
            <FaDownload /> PDF
          </PrimaryBtn>
          <PrimaryBtn onClick={handlePrint}>
            <FaPrint /> Thermal Print
          </PrimaryBtn>
        </Actions>
      </Card>

      {detailOrder && (
        <ModalOverlay onClick={() => setDetailOrder(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Split Payment Details</ModalTitle>
              <IconBtn onClick={() => setDetailOrder(null)}>
                <FaTimes />
              </IconBtn>
            </ModalHeader>
            <ModalBody>
              <DetailLine>Order: #{detailOrder.ordercode}</DetailLine>
              <DetailLine>Bill: {fmtCurrency(detailOrder.totalamount)}</DetailLine>
              {detailOrder.created_at && (
                <DetailLine>Date: {new Date(detailOrder.created_at).toLocaleString("en-IN")}</DetailLine>
              )}
              <DetailBlock>
                {formatSplitLines(detailOrder.split).map((line) => (
                  <DetailSplit key={line}>{line}</DetailSplit>
                ))}
              </DetailBlock>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default SplitPaymentReportPage;

const today = () => toISO(new Date());
const toISO = (d) => toLocalYmd(d);

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;
const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom:16px;padding:26px 28px;border-radius:22px;background:linear-gradient(135deg,#075f9e,#0787c5);box-shadow:0 14px 30px rgba(1,91,154,.17);
  flex-wrap: wrap;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color:#fff;
`;
const Sub = styled.p`
  margin: 6px 0 0;
  color:#dff3ff;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const NavBtn = styled.button`
  border: 1px solid ${COLOR.line};
  background: ${COLOR.card};
  color: ${COLOR.text};
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: ${COLOR.card};
  font-size: 13px;
  color: ${COLOR.muted};
  input {
    border: none;
    outline: none;
    font-size: 13px;
    color: ${COLOR.text};
    background: transparent;
  }
`;
const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 16px;
`;
const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;
const KpiCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
`;
const KpiLabel = styled.div`
  color: ${COLOR.muted};
  font-size: 12px;
  font-weight: 700;
`;
const KpiValue = styled.div`
  color: ${COLOR.text};
  font-size: 16px;
  font-weight: 800;
  margin-top: 4px;
`;
const Center = styled.div`
  padding: 24px 0;
  text-align: center;
  color: ${COLOR.muted};
  font-weight: 600;
`;
const Section = styled.div`
  margin-top: 16px;
`;
const SectionTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 800;
  color: ${COLOR.text};
`;
const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${COLOR.line};
    font-size: 13px;
    text-align: left;
  }
  th {
    background: #f8fafc;
    font-weight: 700;
  }
`;
const OrderGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;
const OrderCard = styled.button`
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
  background: #fff;
  text-align: left;
  cursor: pointer;
`;
const OrderTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
`;
const OrderId = styled.div`
  font-weight: 800;
  color: ${COLOR.text};
`;
const OrderAmount = styled.div`
  font-weight: 800;
  color: ${COLOR.primary};
`;
const OrderDate = styled.div`
  color: ${COLOR.muted};
  font-size: 11px;
  margin-top: 4px;
`;
const OrderLine = styled.div`
  font-size: 12px;
  margin-top: 4px;
  color: ${COLOR.text};
`;
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 999;
`;
const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  max-width: 480px;
  width: 100%;
  padding: 16px;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: ${COLOR.text};
`;
const IconBtn = styled.button`
  border: none;
  background: transparent;
  color: ${COLOR.muted};
  cursor: pointer;
`;
const ModalBody = styled.div`
  margin-top: 10px;
  display: grid;
  gap: 6px;
  font-size: 13px;
  color: ${COLOR.text};
`;
const DetailLine = styled.div``;
const DetailBlock = styled.div`
  border-top: 1px solid ${COLOR.line};
  margin-top: 6px;
  padding-top: 6px;
  display: grid;
  gap: 4px;
`;
const DetailSplit = styled.div`
  font-weight: 600;
`;
